import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { prettyPrintJson } from 'pretty-print-json';
import { BesharpTableFilter } from './besharp-table.filter';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { MenuItem } from 'primeng/api';
import {
  ColumnFilter,
  Table,
  TableFilterEvent,
  TableHeaderCheckboxToggleEvent,
  TableLazyLoadEvent,
  TableModule,
  TableRowSelectEvent,
} from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RatingModule } from 'primeng/rating';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';
import { formatDate } from '@angular/common';

/// <summary>
/// BesharpTableComponent is a generic table component that can be used to display data in a tabular format.
/// It supports features like sorting, pagination, and custom actions.
/// </summary>
/// <remarks>
/// This component is designed to be reusable and can be used in different parts of the application.
/// It is built using Angular and follows best practices for component design.
/// </remarks>

/// <summary>BesharpTableActionDefinition is an interface that defines the structure of an action that can be performed on a table row.
/// It includes properties for the icon, text, style class, callback function, tooltip, and an active function to determine if the action is active for a given row.
/// </summary>
/// <remarks>
/// This interface is used to define the actions that can be performed on a table row.
/// It allows for customization of the actions based on the data in the row.
/// </remarks>
export type BesharpTableActionDefinition = {
  icon: string; // FontAwesome or any other Library icon name
  label: string; // Text to be displayed on the action button
  styleClass: string; // CSS class to be applied to the action button
  callback: any; // Callback function to be executed when the action is triggered
  tooltip?: string; // Tooltip text to be displayed on hover
  active?: (row: any) => boolean; // Function to determine if the action is active for the given row
};

/// <summary>
/// BesharpTableHeaderDefinition is an interface that defines the structure of a table header.
/// It includes properties for the definition, title, JSON formatting, sorting, transformer function, link, virtual function, and tooltip.
/// </summary>
/// <remarks>
/// This interface is used to define the headers of the table.
/// It allows for customization of the headers based on the data in the table.
/// It also allows for the use of custom functions to transform the data or display it in a specific format.
/// </remarks>
export type BesharpTableHeaderDefinition = {
  def: string; // Property key name in the data source
  title: string; // Display name for the header
  json?: boolean; // Indicates if the data is in JSON format
  sortable?: boolean; // Indicates if the column is sortable
  transformer?: (value: any) => string; // Function to transform the value for display
  link?: boolean; // Indicates if the value should be displayed as a link
  linkLabel?: string; // Used to give a label to a link object if any
  virtual?: (row: any) => string; // Function to generate a virtual value for the row that does not exist in the data source
  tooltip?: (row: any) => string; // Function to generate a tooltip for the row
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'; // Tooltip position if any
  columnFilter?: boolean; // Activate standard filters from PrimeNG
  columnFilterType?: string; // PrimeNG filter type (e.g. 'text', 'numeric', 'date', 'boolean'). Defaults to 'text'
  columnFilterOptions?: { label: string; value: string }[]; // Options for multi-select column filter
  multiValueSeparators?: string[]; // If set, the column filter accepts multiple values separated by any of these characters (e.g. [',', ' '])
  image?: boolean; // Set this to true if the value is an image url
  imageLabel?: (row: any) => string; // Label for the image accessibility, defaults to title property if missing
  imageShadow?: boolean; // Add a custom class to the image cell
  currency?: boolean; // Set this to true if the value must be represented as currency (default €)
  currencyCode?: string; // Set this to change the currency type for the currency field
  rating?: boolean;
};

/// <summary>
/// BesharpTableSize is an enumeration that defines the size of the table.
/// It includes three sizes: Small, Medium, and Large.
/// </summary>
/// <remarks>
/// This enumeration is used to define the size of the table.
/// It allows for customization of the table size based on the requirements of the application.
/// </remarks>
export enum BesharpTableSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

@Component({
  selector: 'app-besharp-table',
  templateUrl: './besharp-table.component.html',
  styleUrl: './besharp-table.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    MenuModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    RatingModule,
    SkeletonModule,
    SelectModule,
  ],
})
export class BesharpTableComponent<T> implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  /** Reference to the PrimeNG Table instance */
  @ViewChild('beSharpTable')
  beSharpTable!: Table;

  /** Reference to the search input element (if present) */
  @ViewChild('searchInput')
  searchInput?: HTMLInputElement;

  /** Debounce time in ms for the global search input */
  @Input()
  debounceSearchTime = 500;

  /** Array of items to display in the table */
  @Input()
  tableItems: T[] = [];

  /** Total number of records (for pagination) */
  @Input()
  totalRecords = 0;

  /** Show loading spinner when true */
  @Input()
  loading?: boolean = false;

  /** Name of the column used as unique row identifier (required) */
  @Input()
  idColumnDef!: any;

  /** Title for the actions column */
  @Input()
  actionTitle = '#';

  /** Label for the actions menu */
  @Input()
  actionLabel = 'Actions';

  /** Definitions for row action buttons */
  @Input()
  actionDefinitions?: BesharpTableActionDefinition[] = [];

  /** Column definitions for the table */
  @Input()
  tableDefinitions!: BesharpTableHeaderDefinition[];

  /** Enable pagination if true */
  @Input()
  paginator?: boolean = false;

  /** Number of rows per page */
  @Input()
  pageRows = 3;

  /** Options for rows per page */
  @Input()
  pageRowsOptions: number[] = [3, 6, 8];

  /** Default sort field */
  @Input()
  defaultActive?: string;

  /** Default sort direction (1 = asc, -1 = desc) */
  @Input()
  defaultDirection?: -1 | 1 = -1;

  /** Table size: Small, Medium, or Large */
  @Input()
  tableSize: BesharpTableSize = BesharpTableSize.Medium;

  /** Show grid lines between cells */
  @Input()
  showGridLines?: boolean = false;

  /** Show striped rows */
  @Input()
  showStripes?: boolean = false;

  /** Show current page report */
  @Input()
  showCurrentPageReport?: boolean = false;

  /** Template for current page report */
  @Input()
  currentPageReportTemplate?: string = 'Showing {first} to {last} of {totalRecords} entries';

  /** Fields to use for global search filtering */
  @Input()
  globalFilterFields!: string[] | undefined;

  /** Enable row selection with checkboxes */
  @Input()
  hasCheckboxes: boolean = false;

  /** Maximum number of rows that can be selected simultaneously. Null means no limit. */
  @Input()
  maxSelectable: number | null = null;

  /** Enable row hover effect */
  @Input()
  rowOverEffect: boolean = true;

  /** Show button to clear all filters */
  @Input()
  showClearFiltersButton: boolean = false;

  /** Show button to export table as CSV */
  @Input()
  showExportButton: boolean = false;

  /** Show global search input */
  @Input()
  showSearchInput: boolean = false;

  /** Enable lazy loading (server-side pagination/filtering) */
  @Input()
  lazyMode: boolean = false;

  /** Emits when a row is clicked */
  @Output()
  rowClicked: EventEmitter<any> = new EventEmitter();

  /** Emits when a rating value is changed */
  @Output()
  ratingChanged: EventEmitter<any> = new EventEmitter();

  /** Emits when row selection changes */
  @Output()
  rowsSelectionChangedEvent: EventEmitter<any> = new EventEmitter();

  /** Emits the limit value when the user tries to select more rows than maxSelectable allows */
  @Output()
  selectionLimitReached: EventEmitter<number> = new EventEmitter();

  /** Emits when data should be (re)loaded (e.g. on filter/pagination) */
  @Output()
  requestingDataEvent: EventEmitter<BesharpTableFilter | undefined> = new EventEmitter(undefined);

  /** Emits when the paginator changes */
  @Output() onPageChange: EventEmitter<any> = new EventEmitter<any>();

  /** Emits when the sorting changes */
  @Output() onSortChange: EventEmitter<any> = new EventEmitter<any>();

  /** Emits when the clearing filters */
  @Output() onClearFilters: EventEmitter<any> = new EventEmitter<any>();

  /** Emits when column filters change */
  @Output() onColumnFilter: EventEmitter<any> = new EventEmitter<any>();

  /** Emits when global search changes */
  @Output() onGlobalSearch: EventEmitter<string> = new EventEmitter<string>();

  /** Emits when the editing a field */
  @Output() onEditClickEvent: EventEmitter<any> = new EventEmitter<any>();

  /** Internal: List of column keys for export and configuration */
  protected tableDefinitionHeaders: string[] = [];
  /** Internal: Table size for PrimeNG Table */
  protected size: any = undefined;
  /** Internal: Menu items for row actions */
  protected items: MenuItem[] = [];
  /** Internal: Array of selected/checked rows */
  protected checkedItemsArray: any[] = [];
  /** Internal: Multi-value filter state keyed by column def */
  protected multiValueFilterState: { [key: string]: string[] } = {};

  /** Handles adding values from the multi-value filter input */
  protected onMultiValueInput(event: KeyboardEvent, field: string, separators: string[], filterCallback: any, cf: ColumnFilter): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (event.key === 'Enter') {
      this.addAndApply(input, field, separators, filterCallback, cf);
      return;
    }
    if (separators.some(sep => value.includes(sep))) {
      const regex = new RegExp(`[${separators.map(s => s.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')).join('')}]`);
      const parts = value.split(regex).map(v => v.trim()).filter(v => v.length > 0);
      if (!this.multiValueFilterState[field]) {
        this.multiValueFilterState[field] = [];
      }
      this.multiValueFilterState[field] = [...this.multiValueFilterState[field], ...parts];
      input.value = '';
    }
  }

  /** Shared logic: flush input into chips and apply filter */
  private addAndApply(input: HTMLInputElement, field: string, separators: string[], filterCallback: any, cf: ColumnFilter): void {
    const trimmed = input.value.trim();
    if (trimmed.length > 0) {
      if (!this.multiValueFilterState[field]) {
        this.multiValueFilterState[field] = [];
      }
      const regex = new RegExp(`[${separators.map(s => s.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')).join('')}]`);
      const parts = trimmed.split(regex).map(v => v.trim()).filter(v => v.length > 0);
      this.multiValueFilterState[field] = [...this.multiValueFilterState[field], ...parts];
      input.value = '';
    }
    if (this.multiValueFilterState[field]?.length > 0) {
      filterCallback(this.multiValueFilterState[field]);
    } else {
      filterCallback(null);
    }
    if (cf) {
      cf.hide();
    }
  }

  /** Removes a chip from the multi-value filter (does not apply filter) */
  protected removeMultiValueChip(field: string, index: number, filterCallback: any): void {
    this.multiValueFilterState[field].splice(index, 1);
    this.multiValueFilterState[field] = [...this.multiValueFilterState[field]];
  }

  /** Clears all chips for a multi-value filter and applies */
  protected clearMultiValueFilter(field: string, filterCallback: any, cf: ColumnFilter): void {
    if (cf) {
      cf.hide();
    }
    this.multiValueFilterState[field] = [];
    filterCallback(null);
  }

  /** Applies the current multi-value filter state (same behavior as Enter) */
  protected applyMultiValueFilter(input: HTMLInputElement, field: string, separators: string[], filterCallback: any, cf: ColumnFilter): void {
    this.addAndApply(input, field, separators, filterCallback, cf);
  }

  /** Adds the current input value as chip(s) (triggered by the + button), does NOT apply filter */
  protected addMultiValueFromInput(input: HTMLInputElement, field: string, separators: string[]): void {
    const value = input.value.trim();
    if (value.length > 0) {
      if (!this.multiValueFilterState[field]) {
        this.multiValueFilterState[field] = [];
      }
      const regex = new RegExp(`[${separators.map(s => s.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')).join('')}]`);
      const parts = value.split(regex).map(v => v.trim()).filter(v => v.length > 0);
      this.multiValueFilterState[field] = [...this.multiValueFilterState[field], ...parts];
      input.value = '';
    }
  }

  /** Clears the internal row selection and emits the empty selection event */
  public clearSelection(): void {
    this.checkedItemsArray = [];
    this.rowsSelectionChangedEvent.next([]);
  }

  /** Resets the table: clears all filters, search input, and selection. Does not emit onClearFilters. */
  public resetTable(): void {
    this.beSharpTable.clear();
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.globalSearchValue = '';
    this.multiValueFilterState = {};
    this.clearSelection();
    if (this.lazyMode) {
      this.onGlobalSearch.emit('');
    }
  }
  /** Internal: Column headers for export */
  protected exportColumnHeaders: any[] = [];

  /** Internal: Value of the global search input */
  private globalSearchValue = '';
  /** Internal: Subject for debounced global search */
  private globalFilterSubject = new Subject<string>();

  public dynamicMenuItems$: BehaviorSubject<MenuItem[]> = new BehaviorSubject(
    [] as MenuItem[]
  );

  constructor() {}
  /**
   * Lifecycle: OnInit
   * Initializes table configuration, sets up search debounce, and requests initial data.
   */
  ngOnInit(): void {
    if (!this.idColumnDef) {
      throw new Error(
        'You need to define the "id" column of your datasource, this is useful for rows\' manipulation features'
      );
    }

    // Add the configuration data to define both the columns and the actions for the table
    this.addTableHeadersToTable(this.tableDefinitions);
    this.addActionDefinitionsDataToTable(this.actionDefinitions || [], this.tableDefinitionHeaders);

    // Register the global search filter debounce subject
    this.globalFilterSubject.pipe(debounceTime(this.debounceSearchTime)).subscribe((value) => {
      this.globalSearchValue = value;
      if (this.lazyMode) {
        // Emit event for lazy mode to handle in parent component
        this.onGlobalSearch.emit(value);
      } else {
        this.beSharpTable?.filterGlobal(this.globalSearchValue, 'contains');
      }
    });

    // Request initial data
    this.requestData(undefined);

    // Change the UI size of the Table
    this.size = this.tableSize !== BesharpTableSize.Medium ? this.tableSize : undefined;
  }

  /**
   * Lifecycle: AfterViewInit
   * Assigns the export function to the PrimeNG Table instance.
   */
  ngAfterViewInit(): void {
    this.beSharpTable.exportFunction = this.exportFunction.bind(this);
  }

  /**
   * Lifecycle: OnChanges
   * When tableItems changes from empty to non-empty while a selection exists, force a new
   * checkedItemsArray reference so Angular propagates the change to [selection] on p-table.
   * This makes PrimeNG call updateSelectionKeys() and rebuild selectionKeys after new data loads,
   * so row checkboxes reflect the correct checked state for the current page.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableItems']) {
      const incoming: T[] = changes['tableItems'].currentValue;
      if (incoming && incoming.length > 0 && this.checkedItemsArray.length > 0) {
        this.checkedItemsArray = [...this.checkedItemsArray];
      }
    }
  }

  /**
   * Lifecycle: OnDestroy
   * Unsubscribes from the global search subject to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.globalFilterSubject.unsubscribe();
  }

  /**
   * Adds column headers to the table and prepares export headers.
   * @param tableDefinitions Array of column definitions
   * @param tableDefinitionHeaders (optional) Array to store header keys
   */
  protected addTableHeadersToTable(
    tableDefinitions: BesharpTableHeaderDefinition[],
    tableDefinitionHeaders: string[] = []
  ): void {
    if (!tableDefinitions || tableDefinitions.length === 0) {
      throw new Error('Verify that tableDefinitions is defined in the parent component');
    }

    tableDefinitionHeaders = tableDefinitions.map((i) => i.def);
    this.exportColumnHeaders = tableDefinitions.map((i) => ({ field: i.def, header: i.title }));
  }

  /**
   * Adds action definitions to the table and prepares the actions menu.
   * @param actionDefinitions Array of action definitions
   * @param tableDefinitionHeaders Array of column keys
   */
  protected addActionDefinitionsDataToTable(
    actionDefinitions: BesharpTableActionDefinition[],
    tableDefinitionHeaders: string[]
  ): void {
    if (!actionDefinitions || actionDefinitions.length === 0) {
      throw new Error('Verify that actionDefinitions is defined in the parent component');
    }

    tableDefinitionHeaders = tableDefinitionHeaders.concat('actions');
    this.items = [
      {
        label: this.actionLabel || '',
        items: this.actionDefinitions,
      },
    ];
  }

  /**
   * Clears all filters and resets the table and search input.
   */
  protected clearFilters(): void {
    this.beSharpTable.clear(); // Clear table filters
    if (this.searchInput) {
      this.searchInput.value = ''; // Clear the search input box
    }
    this.globalSearchValue = ''; // Clear the global filter
    this.multiValueFilterState = {}; // Clear multi-value filters
    this.clearSelection(); // Clear row selection
    if (this.lazyMode) {
      this.onGlobalSearch.emit(''); // Emit clear global search event for lazy mode
    }
    this.onClearFilters.emit(); // Emit clear filters event
  }

  /**
   * Formats a JSON value for pretty display in the table.
   * @param json The JSON value to format
   * @returns HTML string with formatted JSON
   */
  protected applyJsonFormatting(json: any): string {
    return prettyPrintJson.toHtml(json);
  }

  /**
   * Returns the label for an image cell, using a custom function or the column title.
   * @param columnDefinition The column definition
   * @param rowData The row data
   * @returns The image label string
   */
  protected setImageLabel(columnDefinition: any, rowData: any): string {
    if (columnDefinition.imageLabel) {
      return columnDefinition?.imageLabel(rowData);
    }
    return columnDefinition?.title;
  }

  /**
   * Emits the ratingChanged event when a rating is changed in the table.
   * @param rowData The row data with the changed rating
   */
  protected ratingChangedEvent(rowData: any): void {
    this.ratingChanged?.next(rowData);
  }

  protected onPrimeNgSelectionChange(newSelection: any[]): void {
    // Ignore spurious empty-selection events emitted while loading (value is also empty).
    if (newSelection.length === 0 && (!this.tableItems || this.tableItems.length === 0)) {
      return;
    }

    // Build the set of IDs for rows currently visible on this page.
    const currentPageIds = new Set(
      (this.tableItems || []).map((item) => (item as any)[this.idColumnDef])
    );

    // Items in the new selection that belong to the current page.
    const currentPageSelected = newSelection.filter((item) =>
      currentPageIds.has(item[this.idColumnDef])
    );

    // Items already selected that are NOT on the current page — preserve them.
    const offPageSelections = this.checkedItemsArray.filter(
      (item) => !currentPageIds.has(item[this.idColumnDef])
    );

    // Merge: keep off-page selections and apply the new current-page selection.
    // This ensures select-all/deselect-all only affects the current page.
    this.checkedItemsArray = [...offPageSelections, ...currentPageSelected];
  }

  /**
   * Handles row selection event and emits the updated selection array.
   * @param event PrimeNG TableRowSelectEvent
   */
  protected rowSelectionEvent(event: TableRowSelectEvent) {
    try {
      if (this.maxSelectable !== null && this.checkedItemsArray.length > this.maxSelectable) {
        const capped = this.checkedItemsArray.slice(0, this.maxSelectable);
        this.selectionLimitReached.emit(this.maxSelectable);
        // Defer so PrimeNG's preventSelectionSetterPropagation flag is already reset when we
        // assign the capped array. That allows ngOnChanges to call updateSelectionKeys() and
        // selectionSource$.next(), which visually unchecks the over-limit row.
        setTimeout(() => {
          this.checkedItemsArray = capped;
          this.rowsSelectionChangedEvent.next(capped);
        });
        return;
      }
      this.rowsSelectionChangedEvent.next(this.checkedItemsArray);
    } catch (err) {
      console.log(
        `there was an error in selecting a new row while retrieving id column for ${this.idColumnDef} with data ${JSON.stringify(event?.data)}: ${err}`
      );
    }
  }

  /**
   * Handles row deselection event and emits the updated selection array.
   * @param event PrimeNG TableRowSelectEvent
   */
  protected rowDeSelectionEvent(event: TableRowSelectEvent): void {
    try {
      this.rowsSelectionChangedEvent.next(this.checkedItemsArray);
    } catch (err) {
      console.log(
        `there was an error in deleting a row while retrieving id column for ${this.idColumnDef} with data ${event?.data}: ${err}`
      );
    }
  }

  /**
   * Handles select/deselect all rows event and emits the updated selection array.
   * @param event PrimeNG TableHeaderCheckboxToggleEvent
   */
  protected manageAllRowsSelection(event: TableHeaderCheckboxToggleEvent): void {
    try {
      if (this.maxSelectable !== null && this.checkedItemsArray.length > this.maxSelectable) {
        const capped = this.checkedItemsArray.slice(0, this.maxSelectable);
        this.selectionLimitReached.emit(this.maxSelectable);
        setTimeout(() => {
          this.checkedItemsArray = capped;
          this.rowsSelectionChangedEvent.next(capped);
        });
        return;
      }
      this.rowsSelectionChangedEvent.next(this.checkedItemsArray);
    } catch (err) {
      console.log(
        `there was an error in select/deselect all for ${this.idColumnDef} with data ${event?.checked}: ${err}`
      );
    }
  }

  /**
   * Handles filter event: clears selection if filtered rows are not visible.
   * @param event PrimeNG TableFilterEvent
   */
  protected searchFilterEvent(event: TableFilterEvent) {
    // Reset selected items as the table does not remember
    // to remove non visible items if they were selected previously.
    if (!this.lazyMode) {
      this.checkedItemsArray = [];
      this.rowsSelectionChangedEvent.next(this.checkedItemsArray);
    }
  }

  /**
   * Handles input event for the global search input, triggers debounced search.
   * @param $event Input event
   */
  protected searchInputRequest($event: any): void {
    if (this.globalFilterFields) {
      this.globalFilterSubject.next($event?.target?.value);
    }
  }

  /**
   * Custom export function for table cells, handles JSON formatting if needed.
   * @param cell The cell object with field and data
   * @returns The value to export as string
   */
  protected exportFunction(cell: any): string {
    const definition = this.tableDefinitions.find((row) => row.def === cell.field);
    if (definition) {
      const isJson = definition.json;
      if (isJson) {
        return JSON.stringify(cell.data);
      } else {
        return cell.data;
      }
    }
    return '';
  }

  /**
   * Handles row click event, emits rowClicked if a table cell is clicked.
   * @param $event Mouse event
   * @param rowData The row data
   */
  protected rowClickedEvent($event: any, rowData: any): void {
    if ($event?.target?.localName === 'td') {
      this.rowClicked.next(rowData);
      console.debug($event, rowData);
    }
  }

  /**
   * Handles lazy loading/filtering event, builds filter object and requests data.
   * @param event PrimeNG TableLazyLoadEvent
   */
  protected filterChangedEvent(event: TableLazyLoadEvent) {
    // const filters = BesharpTableFilter.fromPrimeNgLazyLoadEvent(event);
    // filters.search = this.globalSearchValue;

    // console.debug({ filters: filters });
    // this.requestData(filters);
  }

  /**
   * Emits the requestingDataEvent to request new data (e.g. after filter, sort, or pagination changes).
   * @param filters The filter object (or undefined for initial load)
   */
  protected requestData(filters: BesharpTableFilter | undefined): void {
    this.requestingDataEvent.next(filters);
  }

  /**
   * Formats a date value for display in the table.
   * @param value The date value (string or Date)
   * @returns Formatted date string (dd/MM/yyyy HH:mm)
   */
  protected formatLastConnected(value: string | Date): string {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    return formatDate(date, 'dd/MM/yyyy HH:mm', 'en-GB');
  }

  public showMenu(rowData: any): void {
    this.dynamicMenuItems$.next((this.actionDefinitions || [])
      .filter(action => !action.active || action.active(rowData))
      .map((action) => ({
        label: action.label,
        icon: action.icon,
        command: () => action.callback(rowData),
        styleClass: action.styleClass,
      })));
  }

  public hasActiveActions(rowData: any): boolean {
    return (this.actionDefinitions || []).some(action => !action.active || action.active(rowData));
  }

  handlePageChange(event: any) {
    this.onPageChange.emit(event);
  }

  sortChangedEvent(event: any) {
    this.onSortChange.emit(event);
  }

  onEditClick(event: any): void {
    this.onEditClickEvent.emit(event);
  }

  handleFilterChangedEvent(event: any) {
    console.log('Filter changed:', event);
    this.onColumnFilter.emit(event);
  }

  handleGlobalSearch(event: any) {
    this.onGlobalSearch.emit(event);
  }
}
